import { PointBadge } from '../helpers/PointBadge';
import { PathSelectorModal } from '../paths/PathSelectorModal';
import { CloseIcon, PlusIcon } from '@iconicicons/react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useAnalytics } from '../hooks/useAnalytics';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { UncontrolledTooltip } from 'reactstrap';
import { usePathBuilder } from './usePathBuilder';
import { useCreatePathReportSequenceMutation } from '../services/edgeApi';
import {
  PathReportSelectionContext,
  readLastState,
} from '../utils/pathReportState';
import { PathReport } from '@/types';
import { useSelector } from 'react-redux';
import {
  selectAllPoints,
  selectMapPointsResult,
} from '../map/slice/mapSelectors';
//TODO: remove the deduplicatePaths import and function once the backend is fixed to not return duplicates
import { deduplicatePaths } from '../utils/pathUtils';
import { HorizontalDivider } from '../helpers/HorizontalDivider';
import { AnalyticsEvent } from '../analytics';
import { pathServiceErrorMessage } from './pathServiceErrorMessage';

// const SEPARATOR_HEIGHT = 24;

export type PathBuilderControlsRef = {
  getPaths: () => PathReport[];
};

export type PathBuilderControlsProps = {
  scenarioId?: string;
  gasDate?: string;
  onFulfill?: (data: PathReport[]) => void;
};

export const PathBuilderControls = forwardRef<
  PathBuilderControlsRef,
  PathBuilderControlsProps
>((props, ref) => {
  const analytics = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const allPoints = useSelector(selectAllPoints);
  const mapPointsResult = useSelector(selectMapPointsResult);
  const autoLoadedRef = useRef(false);
  const {
    points = [],
    addPoints,
    removePoint,
    clearPoints,
    newPointPosition,
    setNewPointPosition,
  } = usePathBuilder();
  const [createPathReportSequence, { data, isLoading: isCreatingPath }] =
    useCreatePathReportSequenceMutation();

  const selectionCtx = React.useContext(PathReportSelectionContext);
  const lastState = React.useMemo(() => readLastState() || {}, []);

  const hasCtx = Boolean(selectionCtx);
  const scenarioId = hasCtx
    ? selectionCtx?.selected?.scenario_id
    : props.scenarioId ?? lastState.scenario_id ?? 'default';
  const gasDate = hasCtx
    ? selectionCtx?.selected?.gas_date
    : props.gasDate ??
      lastState.gas_date ??
      new Date().toISOString().slice(0, 10);

  const seqKey = useMemo(() => {
    if (points.length < 2) return null;
    return points.map((p) => p.id).join(',');
  }, [points]);

  const onSubmit = useCallback(async () => {
    if (!seqKey) return;

    const ids = points.map((p) => Number(p.id));
    analytics.logEvent(AnalyticsEvent.BUILT_PATH, {
      scenarioId,
      gasDate,
      points,
    });
    setSearchParams({ sequence: ids.join(',') });

    try {
      const result = await createPathReportSequence({
        sequence: ids,
        best_contracts_only: true,
        scenario_id: scenarioId,
        gas_date: gasDate,
      }).unwrap();
      const paths = deduplicatePaths(result?.paths ?? []);
      props.onFulfill?.(paths);
    } catch (error: any) {
      console.error('Failed to build path:', error);
      toast.error(`Failed to build path: ${pathServiceErrorMessage(error)}`);
    }
  }, [
    seqKey,
    createPathReportSequence,
    points,
    scenarioId,
    gasDate,
    setSearchParams,
  ]);

  // Auto-load path from URL sequence parameter
  useEffect(() => {
    // Only auto-load once when component mounts with a sequence param
    if (autoLoadedRef.current) return;

    const seq = searchParams.get('sequence');
    if (seq) {
      const ids = seq
        .split(',')
        .map((s) => Number(s))
        .filter(Boolean);
      if (ids.length >= 2) {
        // Wait until map points have finished loading before trying to hydrate sidebar points.
        if (mapPointsResult.isUninitialized || mapPointsResult.isLoading) {
          return;
        }

        // Populate the sidebar with the points
        const pointsToAdd = ids.map((id) => allPoints[id]).filter(Boolean);

        if (pointsToAdd.length === ids.length) {
          // All points were found, add them to the builder
          addPoints(pointsToAdd, 0);
          // Mark as auto-loaded after successful hydration.
          autoLoadedRef.current = true;
        } else {
          toast.error('Some points from the URL could not be found');
          // Points finished loading and some IDs are still missing, so don't keep retrying.
          autoLoadedRef.current = true;
        }

        // Also build the path
        createPathReportSequence({
          sequence: ids,
          best_contracts_only: true,
          scenario_id: scenarioId,
          gas_date: gasDate,
        })
          .unwrap()
          .then((result) => {
            const paths = deduplicatePaths(result?.paths ?? []);
            props.onFulfill?.(paths);
          })
          .catch((e) => {
            console.error('Failed to build path from URL:', e);
            toast.error(
              `Failed to build path from URL: ${pathServiceErrorMessage(e)}`,
            );
          });
      }
    }
  }, [
    searchParams,
    scenarioId,
    gasDate,
    createPathReportSequence,
    allPoints,
    mapPointsResult.isUninitialized,
    mapPointsResult.isLoading,
    addPoints,
  ]);

  const lastPoint = useMemo(() => {
    if (
      newPointPosition === null ||
      newPointPosition < 1 ||
      newPointPosition > points.length ||
      points.length === 0
    )
      return null;
    return points[newPointPosition - 1];
  }, [points, newPointPosition]);

  const nextPointLockedTsp = useMemo(() => {
    if (lastPoint === null) return [];
    return [lastPoint.tsp_id];
  }, [lastPoint]);

  useImperativeHandle(
    ref,
    () => {
      return {
        getPaths() {
          return deduplicatePaths(data?.paths ?? []);
        },
      };
    },
    [data?.paths, isCreatingPath],
  );

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h3 className="text-center pt-3">Path</h3>
        <div
          className="pb-2 w-100 d-flex"
          style={{
            position: 'sticky',
            top: -16,
            backgroundColor: 'var(--bs-body-bg)',
            zIndex: 1,
          }}
        >
          <div className="col-2 d-flex justify-content-start align-items-center">
            {points?.length > 0 && (
              <span id="clear-path-button">
                <button
                  className="btn btn-outline-danger"
                  onClick={clearPoints}
                >
                  Clear
                </button>
              </span>
            )}
          </div>
          <div className="col-8 d-flex justify-content-center align-items-center">
            <span id="submit-path-button">
              <button
                disabled={points.length < 2 || isCreatingPath}
                className="btn btn-primary"
                onClick={onSubmit}
              >
                {isCreatingPath ? 'Building Path...' : 'Submit'}
              </button>
            </span>
          </div>
          <div className="col-2"></div>
        </div>
        <div className="d-flex flex-column justify-content-center align-items-center mt-3 gap-2">
          <HorizontalDivider
            onClick={() => setNewPointPosition(0)}
            color="var(--bs-secondary-bg)"
            className="my-3"
          >
            <PlusIcon />
          </HorizontalDivider>

          {points.map((point, index) => (
            <React.Fragment key={point.id + '-' + index}>
              <PointBadge
                point={point}
                extra_text={
                  <>
                    {point.tsp_name}
                    <br />
                    <CloseIcon
                      onClick={(e) => {
                        removePoint(index);
                        e.stopPropagation();
                      }}
                    />
                  </>
                }
              />
              <HorizontalDivider
                onClick={() => setNewPointPosition(index + 1)}
                color="var(--bs-secondary-bg)"
                className="my-3"
              >
                <PlusIcon />
              </HorizontalDivider>
            </React.Fragment>
          ))}

          {points.length < 2 && (
            <UncontrolledTooltip
              placement={'bottom'}
              target="submit-path-button"
            >
              Please add at least two points to build a path
            </UncontrolledTooltip>
          )}
        </div>
      </div>
      <PathSelectorModal
        startingLocation={
          lastPoint !== null
            ? `${lastPoint.tsp_name}: ${lastPoint.name}`
            : undefined
        }
        isOpen={newPointPosition !== null}
        lockedTsps={nextPointLockedTsp}
        onClose={() => setNewPointPosition(null)}
        onSelect={(selected) => {
          if (selected.points.some((point) => !point.node)) {
            toast.error("Can't find paths for unmapped points");
            return;
          }
          addPoints(selected.points, newPointPosition!);
          setNewPointPosition(points.length + selected.points.length);
        }}
        onFinish={() => {
          setNewPointPosition(null);
          onSubmit();
        }}
      />
    </>
  );
});
